/** The admin opened as a Telegram Mini App (docs/admin-miniapp.md, core.telegram.org/bots/webapps).
 *
 *  A Telegram launch is recognised ONLY by `tgWebAppData` in the URL hash — Telegram puts the
 *  signed initData there when it opens the menu button / web_app button URL. Only then is
 *  telegram-web-app.js loaded (never in a normal browser, where the panel stays exactly as it
 *  was). The hash is read once at module load (main.tsx imports this first), before the router can
 *  drop it, and kept in sessionStorage so a reload inside the WebView still knows it is Telegram.
 *
 *  SECURITY: the initData is only a first factor — the server always asks for the TOTP code on
 *  this path (src/api/admin/auth.py `/auth/miniapp`). It is never put into a URL by us and never
 *  logged; it is single-use and short-lived at the server. */

export const TELEGRAM_SCRIPT_SRC = 'https://telegram.org/js/telegram-web-app.js'
const STORAGE_KEY = 'nh_admin_tg_launch'

/** The subset of `window.Telegram.WebApp` the admin uses (Bot API docs, «Initializing Mini Apps»). */
export interface TelegramWebApp {
  initData: string
  version: string
  platform: string
  ready: () => void
  expand: () => void
  isVersionAtLeast: (version: string) => boolean
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void
  /** Bot API 8.0+: a native popup that downloads `url` (no auth headers: a one-time link). */
  downloadFile?: (params: { url: string; file_name: string }, callback?: (accepted: boolean) => void) => void
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
  }
}

export interface TelegramLaunch {
  /** the raw signed initData (the `tgWebAppData` value, URL-decoded) */
  initData: string
  version: string | null
  platform: string | null
}

/** Pure: the launch parameters from a location hash, or null when it is not a Telegram launch. */
export function parseLaunchHash(hash: string): TelegramLaunch | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash
  if (!raw.includes('tgWebAppData=')) return null
  let params: URLSearchParams
  try {
    params = new URLSearchParams(raw)
  } catch {
    return null
  }
  const initData = params.get('tgWebAppData')
  if (!initData) return null
  return { initData, version: params.get('tgWebAppVersion'), platform: params.get('tgWebAppPlatform') }
}

function readStored(): TelegramLaunch | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<TelegramLaunch>
    return typeof v.initData === 'string' && v.initData
      ? { initData: v.initData, version: v.version ?? null, platform: v.platform ?? null }
      : null
  } catch {
    return null
  }
}

function capture(): TelegramLaunch | null {
  if (typeof window === 'undefined') return null
  const fromHash = parseLaunchHash(window.location.hash)
  if (fromHash) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromHash))
    } catch {
      /* storage blocked: this page load still knows */
    }
    return fromHash
  }
  return readStored()
}

const launch: TelegramLaunch | null = capture()

/** Opened from Telegram (this load or earlier in this WebView session). */
export function telegramLaunch(): TelegramLaunch | null {
  return launch
}

export function isTelegramLaunch(): boolean {
  return launch !== null
}

/** The WebApp object once the script has loaded (null in a browser). */
export function telegramWebApp(): TelegramWebApp | null {
  if (!launch || typeof window === 'undefined') return null
  return window.Telegram?.WebApp ?? null
}

/** The initData to send to /auth/miniapp: the script's copy when loaded, else the hash's. */
export function telegramInitData(): string | null {
  return telegramWebApp()?.initData || launch?.initData || null
}

let loading: Promise<TelegramWebApp | null> | null = null

/** Load telegram-web-app.js — only on a Telegram launch — then ready() + expand(). */
export function loadTelegramScript(): Promise<TelegramWebApp | null> {
  if (!launch || typeof document === 'undefined') return Promise.resolve(null)
  if (loading) return loading
  loading = new Promise((resolve) => {
    const done = () => {
      const app = window.Telegram?.WebApp ?? null
      try {
        app?.ready()
        app?.expand()
      } catch {
        /* an old client without expand */
      }
      resolve(app)
    }
    if (window.Telegram?.WebApp) return done()
    const s = document.createElement('script')
    s.src = TELEGRAM_SCRIPT_SRC
    s.async = true
    s.onload = done
    s.onerror = () => resolve(null)
    document.head.appendChild(s)
  })
  return loading
}

/** How a file reaches the operator inside Telegram (pure, for tests). */
export type DownloadMode = 'native' | 'link' | 'blob'

export function downloadMode(app: Pick<TelegramWebApp, 'downloadFile' | 'isVersionAtLeast'> | null): DownloadMode {
  if (!app) return 'blob'
  let modern = false
  try {
    modern = app.isVersionAtLeast('8.0')
  } catch {
    modern = false
  }
  return modern && typeof app.downloadFile === 'function' ? 'native' : 'link'
}

/** Russian text for the /auth/miniapp errors (status + the server's detail). */
export function miniappErrorText(status: number, detail?: string): string {
  if (status === 404) return 'Вход из Telegram выключен. Войдите по email на сайте админки.'
  if (status === 401) {
    if (detail === 'invalid code') return 'Неверный код 2FA. Код можно использовать только один раз.'
    if (detail === 'challenge expired') return 'Время на ввод кода истекло или попытки кончились. Закройте админку и откройте её из бота снова.'
    return 'Telegram не подтвердил вход. Закройте админку и откройте её из бота снова.'
  }
  if (status === 403) {
    if (detail?.includes('totp not enrolled')) return 'У вас не настроена 2FA. Сначала войдите по email на сайте и подключите её.'
    return 'Этот аккаунт Telegram не привязан к оператору админки.'
  }
  if (status === 429) return 'Слишком много попыток. Подождите минуту.'
  if (status === 503) return 'Сервер временно недоступен. Повторите через минуту.'
  return `Не удалось войти (ошибка ${status})`
}

/** The error ends the attempt: the same initData cannot be sent again (single-use), or the
 *  path is closed for this operator. A wrong code (401 «invalid code») is not fatal. */
export function miniappErrorFatal(status: number, detail?: string): boolean {
  if (status === 401) return detail !== 'invalid code'
  return status === 404 || status === 403
}
